/**
 * Maps a `recording_failed` notification's `body` to a concise, translated
 * sentence for the notification drawer — never the raw backend diagnostic.
 *
 * A `recording_failed` notification's `body` is `"<reason_code>"` or
 * `"<reason_code>:<detail>"` — the stable code the backend stored (see
 * `postgres_ingest._notify_recording_failed`), optionally followed by the
 * recorder's own free-text diagnostic (lobby marker text, page URL, "bill
 * zero", internal error strings — whatever the recorder happened to log).
 * That detail is for the backend log and `recording_attempts.error_detail`
 * ONLY: it must never reach a patron's notification drawer, so every code
 * below maps to one fixed, translated sentence and `recordingFailureMessage`
 * never reads the `:<detail>` half of `body` at all — not even for an
 * unrecognised code, which falls back to `unknownFailure` rather than
 * rendering the raw string.
 *
 * Split out of `NotificationBell.tsx` (rather than exported alongside the
 * component) purely so the pure functions can be unit-tested without
 * rendering it, and so a file exporting a React component only exports the
 * component.
 */

// `removed_by_host` shares the `recording_failed` kind but is billable and
// not a failure, so `NotificationBell` gives it the warning tone instead of
// the kind's default red — everything else in that kind is a genuine
// failure. Lives here, next to the codes it is keyed on.
export const RECORDING_PARTIAL_CODES = new Set(['removed_by_host'])

const RECORDING_FAILURE_MESSAGES: Record<string, string> = {
  not_admitted: 'notifications.recordingFailure.botNotAdmitted',
  denied: 'notifications.recordingFailure.botDenied',
  meeting_cancelled: 'notifications.recordingFailure.meetingCancelled',
  removed_by_host: 'notifications.recordingFailure.botRemoved',
  join_failed: 'notifications.recordingFailure.platformUnreachable',
  error: 'notifications.recordingFailure.genericError',
}

// `body` may carry `<code>:<detail>` (see above); this reads just the code,
// which is all the tone and the message lookup need — the detail half is
// discarded here and never looked at again.
export function recordingFailureCode(body: string): string {
  const separator = body.indexOf(':')
  return separator === -1 ? body : body.slice(0, separator)
}

type Translate = (key: string, vars?: Record<string, string | number>) => string

/**
 * The concise, translated sentence for a `recording_failed` notification's
 * `body`. Takes `t` as a parameter rather than calling `useT()` itself so it
 * can be unit-tested directly against known bodies, including ones carrying
 * a `:<detail>` tail, without rendering any component.
 */
export function recordingFailureMessage(body: string, t: Translate): string {
  const code = recordingFailureCode(body)
  const key = RECORDING_FAILURE_MESSAGES[code]
  return t(key ?? 'notifications.recordingFailure.unknownFailure')
}
